import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, TrafficFine } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['FINES_ADMIN', 'LTA_OFFICER'];
const STATES = ['ISSUED', 'PAID', 'DISPUTED', 'COURT', 'ENFORCED', 'CANCELLED'];

/** M21 — traffic fines: issue, pay (SeyPay intent, assisted at the counter), dispute. */
export function Fines() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const [state, setState] = useState('');
  const [nin, setNin] = useState('');
  const [appliedNin, setAppliedNin] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showIssue, setShowIssue] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (state) params.set('state', state);
    if (appliedNin) params.set('nin', appliedNin);
    return `/api/v1/transport/fines?${params.toString()}`;
  }, [state, appliedNin]);

  const listQ = useApiQuery<Page<TrafficFine>>(path);

  return (
    <div>
      <PageHeader
        title="Traffic Fines"
        subtitle="Issue fines, process counter payments, and record disputes (M21 — LTA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedNin(nin);
              }}
            >
              <input className="field-input !w-40" placeholder="NIN…" value={nin} onChange={(e) => setNin(e.target.value)} />
              <select className="field-input !w-auto" value={state} onChange={(e) => setState(e.target.value)}>
                <option value="">All states</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isOfficer ? (
              <button className="btn-primary" onClick={() => setShowIssue(true)}>
                + Issue fine
              </button>
            ) : null}
          </>
        }
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No traffic fines match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ticket no.</th>
                <th className="table-head">Offence</th>
                <th className="table-head">NIN</th>
                <th className="table-head">Amount</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((f) => (
                <tr key={f.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(f.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{f.ticketNo}</td>
                  <td className="table-cell text-xs">{f.offenceCode}</td>
                  <td className="table-cell font-mono text-xs">{f.nin ?? '—'}</td>
                  <td className="table-cell tabular-nums text-xs">{scr(f.amount)}</td>
                  <td className="table-cell">
                    <StatusBadge status={f.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showIssue ? (
        <IssueFineModal
          onClose={() => setShowIssue(false)}
          onCreated={() => {
            setShowIssue(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <FineDetailModal
          id={selectedId}
          canAct={isOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function IssueFineModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [ticketNo, setTicketNo] = useState('');
  const [offenceCode, setOffenceCode] = useState('');
  const [vehicleRegNo, setVehicleRegNo] = useState('');
  const [nin, setNin] = useState('');
  const [amount, setAmount] = useState('');
  const [demerits, setDemerits] = useState('0');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/transport/fines', {
        ticketNo: ticketNo.trim(),
        offenceCode: offenceCode.trim(),
        ...(vehicleRegNo.trim() ? { vehicleRegNo: vehicleRegNo.trim() } : {}),
        ...(nin.trim() ? { nin: nin.trim() } : {}),
        amount: Number.parseFloat(amount),
        demerits: Number.parseInt(demerits, 10) || 0,
        ...(evidenceRef.trim() ? { evidenceRef: evidenceRef.trim() } : {}),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to issue fine');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Issue a traffic fine" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Ticket no.">
          <input className="field-input" value={ticketNo} onChange={(e) => setTicketNo(e.target.value)} />
        </Field>
        <Field label="Offence code">
          <input className="field-input" value={offenceCode} onChange={(e) => setOffenceCode(e.target.value)} placeholder="SPEEDING_10_20" />
        </Field>
        <Field label="Amount (SCR)">
          <input className="field-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Vehicle reg. no. (optional)">
          <input className="field-input" value={vehicleRegNo} onChange={(e) => setVehicleRegNo(e.target.value.toUpperCase())} />
        </Field>
        <Field label="Driver NIN (optional)">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <Field label="Demerit points">
          <input className="field-input" type="number" value={demerits} onChange={(e) => setDemerits(e.target.value)} />
        </Field>
        <Field label="Evidence ref (optional)" className="col-span-2 sm:col-span-3">
          <input className="field-input" value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !ticketNo.trim() || !offenceCode.trim() || !amount} onClick={submit}>
        {busy ? 'Issuing…' : 'Issue fine'}
      </button>
    </Modal>
  );
}

function FineDetailModal({ id, canAct, onClose, onChanged }: { id: string; canAct: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<TrafficFine>(`/api/v1/transport/fines/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [intentRef, setIntentRef] = useState<string | null>(null);
  const [showDispute, setShowDispute] = useState(false);
  const [reason, setReason] = useState('');

  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ intent: { refNo?: string } }>(`/api/v1/transport/fines/${id}/pay`);
      setIntentRef(r.intent?.refNo ?? 'created');
      q.reload();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'PAYMENT_ALREADY_INITIATED') {
        setError('A payment is already in progress for this fine.');
      } else {
        setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Payment failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const dispute = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/transport/fines/${id}/dispute`, { reason: reason.trim() });
      onChanged();
      q.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Dispute failed');
    } finally {
      setBusy(false);
    }
  };

  const f = q.data;

  return (
    <Modal title={f ? `Fine ${f.ticketNo}` : 'Traffic fine'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {intentRef ? (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          SeyPay payment intent created ({intentRef}).
        </div>
      ) : null}
      {f ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="State" value={<StatusBadge status={f.state} />} />
            <Detail label="Offence" value={f.offenceCode} />
            <Detail label="Amount" value={scr(f.amount)} />
            <Detail label="Demerit points" value={String(f.demerits)} />
            <Detail label="NIN" value={f.nin ?? '—'} mono />
            <Detail label="Vehicle id" value={f.vehicleId ?? '—'} mono />
            <Detail label="Payment ref" value={f.payIntentRefNo ?? '—'} mono />
            {f.paidAt ? <Detail label="Paid" value={formatDateTime(f.paidAt)} /> : null}
          </div>
          {f.disputeReason ? <ErrorNote message={`Disputed: ${f.disputeReason}`} /> : null}

          {canAct && (f.state === 'ISSUED' || f.state === 'DISPUTED') ? (
            <div className="flex flex-wrap gap-2">
              <button className="btn-success" disabled={busy || !!f.payIntentRefNo} onClick={pay}>
                {busy ? 'Working…' : f.payIntentRefNo ? 'Payment already initiated' : 'Process counter payment'}
              </button>
              {f.state === 'ISSUED' ? (
                !showDispute ? (
                  <button className="btn-secondary" onClick={() => setShowDispute(true)}>
                    Record dispute
                  </button>
                ) : null
              ) : null}
            </div>
          ) : null}

          {showDispute ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <Field label="Dispute reason">
                <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={dispute}>
                {busy ? 'Recording…' : 'Confirm dispute'}
              </button>
            </div>
          ) : null}

          {!canAct ? <RoleNotice roles={OFFICER_ROLES} /> : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Issued {formatDateTime(f.issuedAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}
